/**
 * CSV Export Utility
 *
 * Provides functionality to generate and download CSV files from JavaScript data
 * Follows RFC 4180 CSV standard for proper escaping and formatting
 */

export interface ICSVData {
  headers: string[];
  rows: any[][];
  metadata?: {
    filename?: string;
    timestamp?: string;
    source?: string;
    question?: string;
    instruction?: string;
    generatedAt?: string;
    truncated?: boolean;
    totalRows?: number;
    responseType?: string;
  };
}

export class CSVExportUtil {

  /**
   * Generate CSV string from data array with optional headers
   * @param data 2D array of data rows
   * @param headers Optional array of column headers
   * @returns CSV formatted string
   */
  public static generateCSV(data: any[][], headers?: string[]): string {
    const rows: string[] = [];

    // Add headers if provided
    if (headers && headers.length > 0) {
      rows.push(headers.map((header) => this.escapeCSV(header)).join(','));
    }

    // Add data rows
    for (const row of data) {
      const escapedRow = row.map((cell) => this.escapeCSV(cell));
      rows.push(escapedRow.join(','));
    }

    return rows.join('\n');
  }

  /**
   * Generate CSV from structured data object
   * @param csvData Structured CSV data with headers and rows
   * @returns CSV formatted string
   */
  public static generateCSVFromData(csvData: ICSVData): string {
    return this.generateCSV(csvData.rows, csvData.headers);
  }

  /**
   * Generate CSV with metadata headers for enhanced context
   * @param csvData Structured CSV data with metadata
   * @returns CSV formatted string with metadata header section
   */
  public static generateCSVWithMetadata(csvData: ICSVData): string {
    const metadataLines: string[] = [];

    // Add metadata header section
    metadataLines.push('# Insight Export Metadata');

    if (csvData.metadata?.question) {
      metadataLines.push(`# Question: "${this.escapeMetadata(csvData.metadata.question)}"`);
    }

    if (csvData.metadata?.instruction) {
      metadataLines.push(`# Instruction: "${this.escapeMetadata(csvData.metadata.instruction)}"`);
    }

    if (csvData.metadata?.generatedAt) {
      metadataLines.push(`# Generated: ${csvData.metadata.generatedAt}`);
    }

    if (csvData.metadata?.source) {
      metadataLines.push(`# Category: ${this.escapeMetadata(csvData.metadata.source)}`);
    }

    // Add response type if available
    const responseType = (csvData.metadata as any)?.responseType;
    if (responseType) {
      metadataLines.push(`# Response Type: ${responseType}`);
    }

    // Add row count information
    if (csvData.metadata?.truncated && csvData.metadata?.totalRows) {
      metadataLines.push(`# Rows: ${csvData.rows.length} (truncated from ${csvData.metadata.totalRows} total)`);
    } else {
      metadataLines.push(`# Rows: ${csvData.rows.length}`);
    }

    metadataLines.push('#');
    metadataLines.push('# Data begins below:');

    // Generate regular CSV content
    const csvContent = this.generateCSV(csvData.rows, csvData.headers);

    // Combine metadata and content
    return metadataLines.join('\n') + '\n' + csvContent;
  }

  /**
   * Escape CSV value according to RFC 4180 standard
   * @param value Any value to be escaped for CSV
   * @returns Properly escaped CSV string
   */
  public static escapeCSV(value: any): string {
    // Handle null, undefined, and empty values
    if (value === null || value === undefined) {
      return '';
    }

    // Convert to string
    const str = String(value);

    // Check if escaping is needed (contains comma, quote, newline, or carriage return)
    const needsEscaping = str.includes(',') ||
                         str.includes('"') ||
                         str.includes('\n') ||
                         str.includes('\r');

    if (needsEscaping) {
      // Escape quotes by doubling them and wrap in quotes
      return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  }

  /**
   * Escape metadata content for comment lines
   * @param value String value to be escaped for metadata
   * @returns Properly escaped metadata string
   */
  public static escapeMetadata(value: string): string {
    if (!value) { return ''; }

    return value
      .replace(/[\r\n]/g, ' ')  // Replace newlines with spaces
      .replace(/"/g, '\\"')     // Escape quotes for metadata lines
      .substring(0, 200)        // Limit metadata length to prevent overly long lines
      .trim();                  // Remove leading/trailing whitespace
  }

  /**
   * Download CSV content as file using browser download
   * @param content CSV content string
   * @param filename Desired filename (should include .csv extension)
   */
  public static downloadFile(content: string, filename: string): void {
    try {
      // Create blob with CSV content and UTF-8 BOM for Excel compatibility
      const BOM = '\uFEFF'; // UTF-8 BOM for proper Excel encoding
      const blob = new Blob([BOM + content], {
        type: 'text/csv;charset=utf-8;',
      });

      // Create temporary download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      // Append to DOM, click, and cleanup
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up object URL to prevent memory leaks
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('CSV download failed:', error);
      throw new Error('Failed to download CSV file. Your browser may not support this feature.');
    }
  }

  /**
   * Download CSV data structure as file
   * @param csvData Structured CSV data
   * @param filename Optional filename override
   */
  public static downloadCSVData(csvData: ICSVData, filename?: string): void {
    // Use metadata-enhanced CSV generation if metadata is available
    const csvContent = csvData.metadata ?
      this.generateCSVWithMetadata(csvData) :
      this.generateCSVFromData(csvData);
    const finalFilename = filename || this.generateDefaultFilename(csvData);

    this.downloadFile(csvContent, finalFilename);
  }

  /**
   * Generate default filename with timestamp
   * @param csvData CSV data for context
   * @returns Generated filename with .csv extension
   */
  public static generateDefaultFilename(csvData: ICSVData): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
    const source = csvData.metadata?.source || 'data';
    const sanitizedSource = source.toLowerCase().replace(/[^a-z0-9]/g, '_');

    return `${sanitizedSource}_${timestamp}.csv`;
  }

  /**
   * Validate CSV data structure
   * @param csvData CSV data to validate
   * @returns true if data is valid, throws error if invalid
   */
  public static validateCSVData(csvData: ICSVData): boolean {
    if (!csvData) {
      throw new Error('CSV data is required');
    }

    if (!Array.isArray(csvData.rows)) {
      throw new Error('CSV data must have rows array');
    }

    if (csvData.headers && !Array.isArray(csvData.headers)) {
      throw new Error('CSV headers must be an array');
    }

    if (csvData.headers && csvData.rows.length > 0) {
      const headerCount = csvData.headers.length;
      const hasInconsistentColumns = csvData.rows.some((row) =>
        Array.isArray(row) && row.length !== headerCount,
      );

      if (hasInconsistentColumns) {
        console.warn('CSV data has inconsistent column counts. Some rows may be padded or truncated.');
      }
    }

    return true;
  }

  /**
   * Check if browser supports CSV download functionality
   * @returns true if browser supports required APIs
   */
  public static isBrowserSupported(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    return !!(
      window.Blob &&
      window.URL &&
      typeof window.URL.createObjectURL === 'function' &&
      typeof document.createElement === 'function'
    );
  }

  /**
   * Convert object array to CSV data structure
   * @param objects Array of objects to convert
   * @param selectedFields Optional array of field names to include
   * @returns Structured CSV data
   */
  public static fromObjectArray(objects: any[], selectedFields?: string[]): ICSVData {
    if (!objects || objects.length === 0) {
      return {
        headers: ['No Data'],
        rows: [['No data available']],
      };
    }

    // Get all possible fields from all objects or use selected fields
    let fields: string[];
    if (selectedFields) {
      fields = selectedFields;
    } else {
      // Collect all unique fields from all objects
      const allFieldsSet = new Set<string>();
      objects.forEach((obj) => {
        Object.keys(obj).forEach((key) => allFieldsSet.add(key));
      });
      fields = Array.from(allFieldsSet);
    }

    // Create headers
    const headers = fields.map((field) =>
      field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
    );

    // Create rows
    const rows = objects.map((obj) =>
      fields.map((field) => obj[field] ?? ''),
    );

    return {
      headers,
      rows,
      metadata: {
        source: 'object_array',
        timestamp: new Date().toISOString(),
      },
    };
  }

  /**
   * Preview CSV content as string (first few rows)
   * @param csvData CSV data to preview
   * @param maxRows Maximum rows to include in preview
   * @returns Preview string
   */
  public static previewCSV(csvData: ICSVData, maxRows: number = 5): string {
    const previewData: ICSVData = {
      headers: csvData.headers,
      rows: csvData.rows.slice(0, maxRows),
      metadata: csvData.metadata,
    };

    const preview = this.generateCSVFromData(previewData);

    if (csvData.rows.length > maxRows) {
      return preview + `\n... and ${csvData.rows.length - maxRows} more rows`;
    }

    return preview;
  }
}

export default CSVExportUtil;
